<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'

  import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

  import { useI18n } from 'vue-i18n'

  // External library imports
  import { createElementId } from '@common/core-layer/utils'

  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import { isCDNOrGCSUrl } from '@common/media-layer/utils/url.utils'

  import CPAvatar from '@ui/components/CPAvatar.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPDivider from '@ui/components/CPDivider.vue'

  import CPProgress from '@ui/components/CPProgress.vue'

  import CPText from '@ui/components/CPText.vue'

  // Internal imports
  import { convertToCompactTime } from '../../core-layer/utils/date.utils'

  import { NotificationTypeIds, SourceApp, type INotification } from '../types/notification.types'

  // Props definitions
  const props = defineProps({
    notification: {
      type: Object as PropType<INotification>,
      required: true,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'onItemClicked', notification: INotification): void
  }>()

  const { t } = useI18n()

  const notificationTime = computed(() => props.notification.relativeTime || convertToCompactTime(props.notification.createdAt))

  const isEventAppNotification = computed(
    () =>
      props.notification.sourceApp === SourceApp.EVENTS &&
      (props.notification.type ?? props.notification.notificationType) === NotificationTypeIds.EVENT_APP_NOTIFICATION
  )

  const notificationTitle = computed(() => (isEventAppNotification.value && props.notification.metaData?.title?.trim()) || '')

  // Event bodies render with `white-space: pre-line`, so outer newlines would show up as blank lines.
  const notificationBody = computed(() => props.notification.body.trim())

  // Events messages can run to 500 characters, so a clamped one can be expanded in place.
  const isEventsNotification = computed(() => props.notification.sourceApp === SourceApp.EVENTS)

  const notificationBodyRef = ref<HTMLElement | null>(null)

  const isBodyExpanded = ref(false)

  const isBodyOverflowing = ref(false)

  const showBodyToggle = computed(() => isEventsNotification.value && (isBodyOverflowing.value || isBodyExpanded.value))

  let bodyResizeObserver: ResizeObserver | null = null

  const useRawUrl = computed(
    () =>
      isCDNOrGCSUrl(props.notification.avatarData?.avatarUrl) && props.notification.avatarData?.avatarUrl.includes('/revex-certificates')
  )

  /** Generic progress chrome — any notification with `metaData.progressBar`. */
  const progress = computed(() => {
    const progressMeta = props.notification.metaData?.progressBar
    if (!progressMeta) return null

    const percent =
      typeof progressMeta.percent === 'number' && Number.isFinite(progressMeta.percent)
        ? Math.min(100, Math.max(0, progressMeta.percent))
        : null
    const label = typeof progressMeta.label === 'string' ? progressMeta.label.trim() : ''

    if (percent === null || !label) return null

    return { percent, label }
  })

  // Content types and their associated CSS classes
  const highlightableContentTypes: Record<string, string[]> = {
    comment: ['italic', 'block'],
    post: ['italic', 'block'],
    contact: ['text-body', 'font-bold'],
    'community-group': ['text-body', 'font-bold'],
    course: ['text-body', 'font-bold'],
    'community-channel': ['text-body', 'font-bold'],
    event: ['text-body', 'font-bold'],
    role: ['text-body', 'font-bold'],
    clientPortalUser: ['text-body', 'font-bold'],
    'client-portal-shared-file': ['text-body', 'font-bold'],
    'shared-by': ['text-body', 'font-bold'],
    'client-portal-invoice': ['text-body', 'font-bold'],
    'client-portal-estimate': ['text-body', 'font-bold'],
  }

  onMounted(() => {
    void nextTick(measureBodyOverflow)
    // Items can mount before the drawer is laid out, so measure again once the body gets its size.
    if (!isEventsNotification.value || !notificationBodyRef.value || typeof ResizeObserver === 'undefined') return
    bodyResizeObserver = new ResizeObserver(() => measureBodyOverflow())
    bodyResizeObserver.observe(notificationBodyRef.value)
  })

  onBeforeUnmount(() => {
    bodyResizeObserver?.disconnect()
    bodyResizeObserver = null
  })

  function measureBodyOverflow() {
    const bodyElement = notificationBodyRef.value
    if (!isEventsNotification.value || !bodyElement || isBodyExpanded.value) return
    isBodyOverflowing.value = bodyElement.scrollHeight > bodyElement.clientHeight + 1
  }

  function toggleBodyExpanded() {
    isBodyExpanded.value = !isBodyExpanded.value
    if (!isBodyExpanded.value) void nextTick(measureBodyOverflow)
  }

  // User-defined functions

  function handleClick() {
    emit('onItemClicked', props.notification)
  }

  // Function to identify and highlight content in notifications
  function identifyAndHighlightContent(notificationBodyElement: HTMLElement) {
    if (!notificationBodyElement) return

    // Find all span elements with data-type attributes
    const notificationBodySpans = notificationBodyElement.querySelectorAll('span[data-type]')

    if (!notificationBodySpans || notificationBodySpans.length === 0) return

    notificationBodySpans.forEach(span => {
      const spanType = span.getAttribute('data-type')

      // Get classes for this content type from the map
      if (spanType && highlightableContentTypes[spanType]) {
        const classes = highlightableContentTypes[spanType]
        classes.forEach(className => {
          span.classList.add(className)
        })
      }
    })
  }
</script>

<template>
  <div
    :id="createElementId('notification', 'item', notification._id)"
    class="notification-item relative w-full cursor-pointer bg-surface transition-colors duration-150 hover:bg-hover focus-visible:bg-hover"
    :class="notification.isRead ? 'bg-transparent' : 'notification-unread'"
    :data-name="notification.isRead ? undefined : 'notification-unread'"
    tabindex="0"
    role="button"
    :aria-label="t('notifications.notificationItem')"
    @click.stop="handleClick()"
    @keyup.enter="handleClick()"
  >
    <div class="flex gap-3 box-border px-3 py-3.5">
      <div class="relative flex-shrink-0">
        <div class="relative">
          <CPAvatar
            :id="createElementId('notification', 'avatar', notification._id)"
            size="md"
            :src="notification.avatarData.avatarUrl"
            :title="notification.avatarData.name || t('notifications.user')"
            class="flex h-12 w-12 items-center justify-center"
            :use-raw-url="useRawUrl"
          />
          <div v-if="notification.avatarData.badgeUrl" class="notification-avatar-badge-shell" aria-hidden="true">
            <OptimisedImage
              :id="createElementId('notification', 'avatar-badge', notification._id)"
              :src="notification.avatarData.badgeUrl"
              :alt="t('notifications.notificationBadge')"
              :width="20"
              :height="20"
              img-class="notification-avatar-badge h-full w-full object-contain"
            />
          </div>
        </div>
      </div>

      <div class="flex min-w-0 flex-1 items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <div class="min-w-0">
            <CPText
              v-if="notificationTitle"
              data-name="event-notification-title"
              size="md"
              weight="semibold"
              class="mb-1 text-body leading-6"
            >
              {{ notificationTitle }}
            </CPText>
            <CPText size="md" weight="regular" class="notification-content text-body leading-6">
              <div
                ref="notificationBodyRef"
                v-sanitized-html="{ html: notificationBody, afterSanity: identifyAndHighlightContent }"
                class="notification-body-html"
                :class="[
                  { 'line-clamp-5': !isBodyExpanded && progress, 'line-clamp-3': !isBodyExpanded && !progress },
                  { 'notification-body-html--event': isEventAppNotification },
                ]"
              ></div>
            </CPText>

            <div
              v-if="progress"
              class="notification-progress mt-2.5 flex w-full min-w-0 items-center gap-3"
              data-name="notification-progress"
            >
              <CPProgress
                :id="createElementId('notification', 'progress', notification._id)"
                type="line"
                :percentage="progress.percent"
                :show-indicator="false"
                :height="6"
                status="default"
                class="notification-progress-bar min-w-0 flex-1"
                aria-hidden="true"
              />
              <CPText size="md" weight="regular" class="notification-progress-label shrink-0 whitespace-nowrap text-muted leading-6">
                {{ progress.label }}
              </CPText>
            </div>
          </div>

          <!-- The wrapper stops the toggle's click and Enter from opening the notification. -->
          <span v-if="showBodyToggle" class="notification-body-toggle-shell" @click.stop @keyup.enter.stop @keydown.enter.stop>
            <CPButton
              :id="createElementId('notification', 'body-toggle', notification._id)"
              type="text"
              size="xs"
              class="notification-body-toggle"
              data-name="notification-body-toggle"
              :aria-expanded="isBodyExpanded ? 'true' : 'false'"
              @click="toggleBodyExpanded"
            >
              {{ isBodyExpanded ? t('notifications.showLess') : t('notifications.showMore') }}
            </CPButton>
          </span>

          <CPText size="sm" class="mt-2 text-muted">
            {{ notificationTime }}
          </CPText>

          <slot name="actions" />
        </div>

        <div class="flex min-h-6 flex-shrink-0 items-start pt-1">
          <div v-if="!notification.isRead" data-name="notification-unread-dot" class="notification-unread-dot" />
        </div>
      </div>
    </div>
    <CPDivider margin-bottom="0px" margin-top="0px" />
  </div>
</template>

<style scoped>
  .notification-item {
    outline: none;
    background-color: var(--cp-background-500);
  }

  .notification-content {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .notification-body-html {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .notification-body-html--event {
    white-space: pre-line;
  }

  .notification-body-toggle-shell {
    display: inline-flex;
    margin-top: 4px;
  }

  .notification-body-toggle-shell :deep(.notification-body-toggle) {
    padding: 0;
    height: auto;
  }

  .notification-body-html :deep(.block) {
    display: block;
    margin-top: 4px;
  }

  .notification-body-html :deep(.italic) {
    font-style: italic;
    color: var(--cp-font-700);
  }

  .notification-unread {
    background-color: var(--cp-background-500);
  }

  .notification-avatar-badge-shell {
    position: absolute;
    right: -3px;
    bottom: -3px;
    height: 20px;
    width: 20px;
    border-radius: 999px;
    border: 2px solid var(--cp-background-500);
    background-color: var(--cp-background-500);
    object-fit: contain;
    box-shadow: 0 4px 8px var(--cp-shadow);
    pointer-events: none;
  }

  .notification-avatar-badge {
    display: block;
  }

  .notification-progress :deep(.hr-progress-wrapper) {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
  }

  .notification-progress :deep(.hr-progress),
  .notification-progress :deep(.n-progress),
  .notification-progress :deep(.hr-progress-graph),
  .notification-progress :deep(.n-progress-graph) {
    width: 100%;
  }

  .notification-unread-dot {
    height: 10px;
    width: 10px;
    border-radius: 999px;
    background-color: var(--cp-primary-500);
    box-shadow: 0 0 0 4px var(--cp-primary-50);
  }
</style>
